// src/utils/courierAvatar.js
/**
 * Generate courier initials from name (1-2 uppercase letters)
 * @param {string} name - Courier name
 * @returns {string} 1-2 uppercase letters
 */
export const getInitials = (name) => {
  if (!name || typeof name !== 'string') return '?';
  
  const trimmed = name.trim().toUpperCase();
  const parts = trimmed.split(/\s+/);
  
  if (parts.length === 0) return '?';
  if (parts.length === 1) {
    // Single word: take first 2 letters
    return trimmed.substring(0, 2);
  }
  
  // Multiple words: take first letter of first and second word
  return `${parts[0][0]}${parts[1][0]}`;
};

/**
 * Generate a consistent color for courier avatar based on name
 * @param {string} name - Courier name
 * @returns {string} Hex color code
 */
export const getAvatarColor = (name) => {
  const colors = [
    '#FF6B6B', // Red
    '#4ECDC4', // Teal
    '#45B7D1', // Blue
    '#FFA07A', // Salmon
    '#98D8C8', // Mint
    '#F7DC6F', // Yellow
    '#BB8FCE', // Purple
    '#85C1E2', // Sky Blue
    '#F8B88B', // Peach
    '#A8E6CF', // Light Green
  ];
  
  if (!name) return colors[0];
  
  // Generate hash from name to get consistent color
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  
  const colorIndex = Math.abs(hash) % colors.length;
  return colors[colorIndex];
};

/**
 * Create courier avatar element (either logo or initials)
 * @param {Object} courier - Courier object with name and logo
 * @returns {Object} { type: 'logo'|'initials', content: string|JSX }
 */
export const getCourierAvatar = (courier) => {
  if (courier?.logo && courier.logo.trim()) {
    return {
      type: 'logo',
      url: courier.logo,
      alt: courier.name || 'Courier'
    };
  }
  
  return {
    type: 'initials',
    initials: getInitials(courier?.name),
    backgroundColor: getAvatarColor(courier?.name)
  };
};
